import React, { useCallback, useMemo } from "react";

import * as FleetSelectors from "../../../../store/Fleet/Fleet.selector";
import * as UserSelectors from "../../../../store/User/User.selector";
import * as GroupUtils from "../../../../lib/util/GroupUtils";
import * as GlobalActions from "../../../../store/Global/Global.action";
import * as GroupActions from "../../../../store/Groups/Groups.action";
import * as FleetActions from "../../../../store/Fleet/Fleet.action";
import * as SupportActions from "../../../../views/Support/Support.action";
import * as Flags from "../../../../lib/Flags";
import * as Consts from "../../../../Config";
import * as NavigationUtils from "../../../../lib/util/NavigationUtils";
import * as FileUtils from "../../../../lib/util/FileUtils";
import * as AuthUtils from "../../../../lib/util/AuthUtils";

import Select from "../../../form/Select/Select";
import FlagControl from "../../../admin/FlagControl/FlagControl";
import { useDispatch, useSelector } from "../../../../lib/hook/ReduxHooks";
import { useRoute } from "lib/hook/NavigationHooks";

const DebugPanelScopeSelect: React.FC = () => {
	const dispatch = useDispatch();
	const route = useRoute();

	const { fleets, groups, scope } = useSelector((state: any) => ({
		fleets: FleetSelectors.getAllFleets(state),
		groups: FleetSelectors.selectCurrentScopeFleetGroups(state),
		scope: UserSelectors.selectCurrentScope(state),
	}));

	const { fleetId } = scope;
	const isCSMSuperAdmin = AuthUtils.isCSMSuperAdmin();

	const { groupsLoading, groupList } = useMemo(() => {
		const groupsLoading = groups.pending;
		const groupList: any[] = GroupUtils.flattenGroups(groups.value || []);
		groupList.unshift({ id: null, name: "none selected" } as any);
		return { groupsLoading, groupList };
	}, [groups]);

	const handleFleetChange = useCallback(
		(option: any) => {
			dispatch(GlobalActions.setScope({ fleetId: option.value, group: null }));
			NavigationUtils.refresh();

			if (route?.id === "files") {
				FileUtils.runFileManagerEnabledCheck(route, option.value);
			}
		},
		[dispatch, route],
	);

	const handleGroupChange = useCallback(
		(option: any) => {
			let groupId = option && option.value;
			if (!groupId || groupId.startsWith("-")) {
				groupId = null;
			}
			dispatch(GroupActions.changeScopeGroup(groupId));
		},
		[dispatch],
	);

	const handleShowArchivedChanged = useCallback(() => {
		dispatch(FleetActions.getAllFleets.submit());
		dispatch(SupportActions.getFleets.submit());
	}, [dispatch]);

	const filteredFleets = useMemo(() => {
		if (!fleets.value) return [];
		if (isCSMSuperAdmin) {
			return fleets.value.filter(
				(fleet: any) => fleet.id !== Flags.DEMO_FLEET_ID,
			);
		}
		return fleets.value;
	}, [fleets.value, isCSMSuperAdmin]);

	return (
		<>
			<Select
				className={"DebugPanel__select"}
				fixed={true}
				dev={true}
				label={"Fleet"}
				compact={true}
				onChange={handleFleetChange}
				loading={!fleets.resolved && !fleets.rejected}
				value={fleetId}
				required={true}
				options={(filteredFleets || []).map((fleet) => ({
					value: fleet.id,
					className: fleet.archived ? "--col-important-steel" : "",
					label: `${fleet.name} [${fleet.id}]`,
				}))}
			/>
			{Consts.ARCHIVE_FLEET_ENABLED && (
				<FlagControl
					onChange={handleShowArchivedChanged}
					checkbox={true}
					label={"Show archived fleets"}
					flag={Flags.SHOW_ARCHIVED_FLEETS}
				/>
			)}
			<Select
				className={"DebugPanel__select"}
				fixed={true}
				dev={true}
				label={"Group"}
				compact={true}
				onChange={handleGroupChange}
				value={scope.group || null}
				loading={groupsLoading}
				options={groupList.map((group) => ({
					value: group.id,
					name: "none",
					label: `${group.name} [${group.id || "__all"}]`,
				}))}
			/>
		</>
	);
};

export default React.memo(DebugPanelScopeSelect);
